import { Component, OnInit, OnDestroy, ViewChild, DoCheck } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Table } from 'primeng/table';

import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { PaymentsService } from '@app/services/net-remit/payments.service';
import { SharedService } from '@app/services/net-remit/shared-functions.service';

import { ConfirmationService, MessageService as PrimeMessageService } from 'primeng/api';

import { MemoPaymentSimpleView } from '@app/shared/models/MemoPaymentSimpleView';
import { MemoPaymentInfoView } from '@app/shared/models/MemoPaymentInfoView';
import { MemoPaymentView } from '@app/shared/views/MemoPaymentView';
import { RelatedDocsView } from '@app/shared/views/RelatedDocsView';
import { MemoDocsView } from '@app/shared/views/MemoDocsView';
import { MemoRelatedDocsView, MemoView } from '@app/shared/views/MemoRelatedDocsView';
import { MemoStatusView } from '@app/shared/views/MemoStatusView';
import { routesConstants } from '@app/shared/models/LinksView';

import { finalize, takeWhile } from 'rxjs/operators';
import { fadeIn } from 'assets/animations/animations';
import { default as constantsFile } from '@app/shared/constants.json';

@Component({
    selector: 'app-payment-management-detail',
    templateUrl: './payment-management-detail.component.html',
    styleUrls: ['./payment-management-detail.component.scss'],
    animations: [fadeIn],
    standalone: false
})

export class PaymentManagementDetailComponent implements OnInit, OnDestroy, DoCheck {

  alive: boolean = true;
  constants: any = constantsFile.constants;
  entryFileDisable: boolean;
  hasError: boolean;
  isAdmin: boolean;
  isDownloadError: boolean;
  isLoading: boolean = true;
  rejectFileEnable: boolean;
  reprocessableEnable: boolean;
  sendBspLinkEnable: boolean;
  cancelEnable: boolean;

  downloadedFileName: string;
  paymentDetailTitle: string;

  customerId: number;
  paymentId: number;

  downloadedBlob: Blob;

  memoPayment: MemoPaymentView;

  // memos list
  showMemosList: boolean;
  isLoadingMemoList: boolean = true;
  isLoadingRelatedList: boolean = true;
  index: number = 0;
  memoDocs: Array<MemoDocsView> = new Array<MemoDocsView>();
  relatedDocs: Array<RelatedDocsView> = new Array<RelatedDocsView>();
  amountGrouped: string;
  amountGroupedByCurrency: string;
  amountGroupedCurrency: string;
  searchValueMemoDoc: string;
  searchValueRelatedDoc: string;

  callOnlyOnceMemDoc: boolean;
  callOnlyOnceRelDoc: boolean;

  @ViewChild('memDoc') private tableMemDoc: Table;
  @ViewChild('relDoc') private tableRelDoc: Table;

  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthenticationService,
    private paymentsService: PaymentsService,
    private primeMessageService: PrimeMessageService,
    private translateService: TranslateService,
    private router: Router,
    private sharedService: SharedService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit() {
    this.paymentId = this.activatedRoute.snapshot.params['paymentId'];
    this.customerId = this.activatedRoute.snapshot.params['customerId'];
    this.isAdmin = this.authService.credentials.roleName.includes(this.constants.roleNames.admin);
    this.searchValueMemoDoc    = localStorage.getItem("memoDocFilter") ? localStorage.getItem("memoDocFilter") : "";
    this.searchValueRelatedDoc = localStorage.getItem("relatedDocFilter") ? localStorage.getItem("relatedDocFilter") : "";

    this.getPaymentDetailData();
  }

  ngDoCheck() {
    if (this.tableMemDoc != undefined && !this.callOnlyOnceMemDoc && this.showMemosList) {
      this.callOnlyOnceMemDoc = true;
      this.filterTableMemDoc(this.searchValueMemoDoc);
    }
    if (this.tableRelDoc != undefined && !this.callOnlyOnceRelDoc && this.showMemosList) {
      this.callOnlyOnceRelDoc = true;
      this.filterTableRelDoc(this.searchValueRelatedDoc);
    }
  }

  public filterTableMemDoc(searchValue: string) {
    this.tableMemDoc.filterGlobal(searchValue, 'contains');
    setTimeout(() => {
      this.isLoadingMemoList = false;
    }, 300);
  }

  public filterTableRelDoc(searchValue: string) {
    this.tableRelDoc.filterGlobal(searchValue, 'contains');
    setTimeout(() => {
      this.isLoadingRelatedList = false;
    }, 300);
  }

  public getPaymentDetailData(){
    this.isLoading = true;
    this.hasError = false;
    this.paymentsService.findPaymentDetail(this.paymentId, this.isAdmin ? this.customerId : null)
      .pipe(finalize(() => this.isLoading = false ), takeWhile(() => this.alive))
      .subscribe({ next: (data) => {
        this.hasError = false;
        this.setMemoPayment(data);
      }, error: () => {
        this.hasError = true;
        this.primeMessageService.add({
          closable: true,
          severity:'error',
          summary: this.translateService.instant('Modal.Error'),
          detail: this.translateService.instant('Error Generic Onload')
        });
      }});
  }

  private setMemoPayment(data: MemoPaymentView) {
    this.memoPayment = data;

    const emptyPayment: string = "---";
    this.memoPayment.fromDocument = this.memoPayment.fromDocument === "-1" ? emptyPayment : this.memoPayment.fromDocument.toString().padStart(10, "0");
    this.memoPayment.toDocument = this.memoPayment.toDocument === "-1" ? emptyPayment : this.memoPayment.toDocument.toString().padStart(10, "0");
    this.memoPayment.fromDocumentAdm = this.memoPayment.fromDocumentAdm === "-1" ? emptyPayment : this.memoPayment.fromDocumentAdm.toString().padStart(10, "0");
    this.memoPayment.toDocumentAdm = this.memoPayment.toDocumentAdm === "-1" ? emptyPayment : this.memoPayment.toDocumentAdm.toString().padStart(10, "0");

    this.entryFileDisable = this.paymentsService.isEntryFileDisabled(this.memoPayment.memoStatus.memoStatusId);

    // Se visualiza el botón de Enviar si se cumplen las siguientes condiciones:
    // - Si el estado es alguno de estos: unexpected-error, unable-to-connect, connection-timed-out, connection-rejected, pendingAudit.
    // - Si tiene rol auditor o administrador.
    this.sendBspLinkEnable  = this.paymentsService.canSendToBspLink(this.authService.credentials.roleName, this.memoPayment.memoStatus.memoStatusId);
    this.cancelEnable = this.memoPayment.memoStatus.status == this.constants.memoStatus.prepared
    || this.memoPayment.memoStatus.status == this.constants.memoStatus.pendingAudit;

    this.rejectFileEnable = this.memoPayment.memoStatus.status == this.constants.memoStatus.prepared
    || this.memoPayment.memoStatus.status == this.constants.memoStatus.pending
    || this.memoPayment.memoStatus.status == this.constants.memoStatus.errorCreatingFile;

    this.reprocessableEnable = this.memoPayment.memoStatus.status == this.constants.memoStatus.errorExtractingData;

    this.memoPayment.memoStatus.status     = this.translateService.instant('MemoPayments.' + this.memoPayment.memoStatus.sStatusComplete);
    this.paymentDetailTitle = this.translateService.instant('MemoPayments.Payment Detail Of') + ' ' + this.memoPayment.fileName;

    if (this.memoPayment.oMemosList !== null) {
      this.getMemoDocs(this.memoPayment.oMemosList);
    } else {
      this.isLoadingMemoList = false;
      this.isLoadingRelatedList = false;
    }
  }

  private getMemoDocs(memosList: MemoRelatedDocsView) {
    const memoDocsTmp: Array<MemoDocsView> = new Array<MemoDocsView>();
    const relatedDocsTmp: Array<RelatedDocsView> = new Array<RelatedDocsView>();
    const amountGroupedByCurrencyMap: Map<string, string> = new Map<string, string>();
    memosList.content.agent.forEach(a => {
      const memos: Array<MemoView> = a.memo;
      memos.map(a => {
        const sumValue = parseFloat(amountGroupedByCurrencyMap.get(a.currencyObject.currency));
        const totalValue = sumValue? sumValue + a.incentivesAmount : a.incentivesAmount;
        amountGroupedByCurrencyMap.set(a.currencyObject.currency, totalValue.toFixed(memos[0].currencyObject.decimals));
      });
      memos.forEach(m => {
        const valueSplitted = m.incentivesAmount.toString().split(".");
        if(valueSplitted.length === 1) valueSplitted.push("0");
        const formattedValueTmp = m.currencyObject.decimals > 0 ? valueSplitted[0] + "." + valueSplitted[1].padEnd(m.currencyObject.decimals, "0") : valueSplitted[0];
        const formattedValue: number = +formattedValueTmp;
        memoDocsTmp.push(new MemoDocsView(m.docNumber, a.code, formattedValue, memosList.vat + "%", m.currencyObject.currency + m.currencyObject.decimals));

        if (m.relatedList !== null) {
          m.relatedList.forEach(rl => {
            rl.related.forEach(r => {
              const formattedValueTmp = r.incentiveAmount.toFixed(memos[0].currencyObject.decimals);
              const formattedValue: number = +formattedValueTmp;
              relatedDocsTmp.push(new RelatedDocsView(m.docNumber, r.document, r.issuer, formattedValue, m.currencyObject.currency + m.currencyObject.decimals));
            });
          });
        }
      });
    });

    for (const key of amountGroupedByCurrencyMap.keys()) {
      this.amountGroupedByCurrency = ((this.amountGroupedByCurrency === undefined) ? '' : this.amountGroupedByCurrency + ', ' ) + amountGroupedByCurrencyMap.get(key) + " " + key;
    }

    //Pending refactor
    this.amountGrouped         = parseFloat(this.amountGroupedByCurrency.substring(0, this.amountGroupedByCurrency.indexOf(" "))).toFixed(2);
    this.amountGroupedCurrency = this.amountGroupedByCurrency.substring(this.amountGroupedByCurrency.indexOf(" "), this.amountGroupedByCurrency.length);

    this.memoDocs = memoDocsTmp;
    this.relatedDocs = relatedDocsTmp;
    this.isLoadingMemoList = false;
    this.isLoadingRelatedList = false;
  }

  public showHideMemosList() {
    this.showMemosList = !this.showMemosList;
    if (this.showMemosList) {
      if (this.index == 0) {
        this.isLoadingMemoList = true;
        this.callOnlyOnceMemDoc = false;
      } else {
        this.isLoadingRelatedList = true;
        this.callOnlyOnceRelDoc = false;
      }
    }
  }

  public reprocessPayment() {
    this.reprocessableEnable = false;
    const memoPaymentInfoView = new MemoPaymentInfoView(this.paymentId, this.memoPayment.customer.customerId);
    this.paymentsService.reprocessPayment(memoPaymentInfoView)
      .pipe(finalize(() => {this.isLoading = false; } ), takeWhile(() => this.alive))
      .subscribe({ next: (paymentSaved) => {
        this.hasError = false;
        this.primeMessageService.add({
          closable: true,
          severity:'success',
          summary: this.translateService.instant('Modal.Success'),
          detail: this.translateService.instant('MemoPayments.Payment Single Saved', { param: paymentSaved.fileName })
        });
      },error: () => {
        this.primeMessageService.add({
          closable: true,
          severity:'error',
          summary: this.translateService.instant('Modal.Error'),
          detail: this.translateService.instant('MemoPayments.Error Reprocessing Payment')
        });
      }});
  }

  public cancelPayment() {
    const memoPaymentInfoView = new MemoPaymentInfoView(this.paymentId, this.memoPayment.customer.customerId);
    this.paymentsService.cancelPayment(this.paymentId, memoPaymentInfoView)
      .pipe(finalize(() => { this.isLoading = false, this.cancelEnable = false, this.sendBspLinkEnable = false }), takeWhile(() => this.alive))
      .subscribe({
        next: () => {
          this.hasError = false;
          // Refresh payment detail
          this.memoPayment.fromDocument = "0";
          this.memoPayment.toDocument = "0";
          this.cancelEnable = false;
          this.rejectFileEnable = false;
          this.memoPayment.memoStatus = new MemoStatusView();
          this.memoPayment.memoStatus.memoStatusId = this.constants.memoStatusId.cancelled;
          this.memoPayment.memoStatus.status = this.constants.memoStatus.cancelled;
          // Toast success message
          this.primeMessageService.add({
            closable: true,
            severity: 'success',
            summary: this.translateService.instant('Modal.Success'),
            detail: this.translateService.instant('MemoPayments.cancelled')
          })
        }, error: () => {
          this.hasError = true;
          this.showMemosList = false;
          this.primeMessageService.add({
            closable: true,
            severity: 'error',
            summary: this.translateService.instant('Modal.Error'),
            detail: this.translateService.instant('Error Cancelling Payment')
          });
        }
      }
    );
  }

  public cancelPaymentAlert() {
    this.confirmationService.confirm({
      message: this.translateService.instant('Text Cancel Payment'),
      header: this.translateService.instant('Confirmation'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('Yes'),
      rejectLabel: this.translateService.instant('No'),
      accept: () => {
        this.cancelPayment();
      }}
    );
  }

  public updateStatus(status: string) {
    this.isLoading = true;
    const memoPaymentSimple = new MemoPaymentSimpleView(status, this.isAdmin ? this.customerId : null);
    this.paymentsService.updateStatus(this.paymentId, memoPaymentSimple)
      .pipe(finalize(() => {
        this.isLoading = false;
        if (status === this.constants.memoStatus.cancelled) {
          this.rejectFileEnable = false;
          this.cancelEnable = false;
        }
      }), takeWhile(() => this.alive))
      .subscribe({
        next: () => {
          this.hasError = false;
          this.showMemosList = false;
          this.memoPayment.memoStatus.memoStatusId = this.constants.memoStatus[status];
          this.memoPayment.memoStatus.status = this.translateService.instant('MemoPayments.' + status + '-Complete');
          this.memoPayment.memoStatus.sStatusComplete = status + '-Complete';
          if (status === this.constants.memoStatus.sending) {
            this.sendBspLinkEnable = false;
            this.primeMessageService.add({
              closable: true,
              severity: 'success',
              summary: this.translateService.instant('Modal.Success'),
              detail: this.translateService.instant('MemoPayments.Sending BspLink')
            });
          }
        },
        error: () => {
          this.hasError = true;
          this.primeMessageService.add({
            closable: true,
            severity: 'error',
            summary: this.translateService.instant('Modal.Error'),
            detail: this.translateService.instant('Error Updating Status File')
          });
        }
      });
  }

  public getFileToDownload(typeFile: string) {
    this.paymentsService.downloadPaymentsFiles(typeFile, this.memoPayment.memoPaymentId, this.isAdmin ? this.customerId : null)
			.pipe(finalize(() => this.isLoading = false ), takeWhile(() => this.alive))
			.subscribe({ next: (data) => {
        this.isDownloadError = false;
        this.downloadedFileName = data.fileName;
        this.downloadedBlob = this.sharedService.getBlob(data.fileContent, data.mimeType);
        this.sharedService.downloadFile(this.downloadedBlob, this.downloadedFileName);
			}, error: () => {
        this.isDownloadError = true;
        this.primeMessageService.add({
          closable: true,
          severity:'error',
          summary: this.translateService.instant('Modal.Error'),
          detail: this.translateService.instant('Error File Download')
        });
      }});
  }

  public viewTaskDetail(memoPayment: MemoPaymentView) {
    this.router.navigate([routesConstants.paymentManagement.paymentManagement + '/' + routesConstants.paymentManagement.taskDetail + '/' + memoPayment.parentId + '/customer/' + this.customerId]);
  }

  public clearMemoDoc(table: Table) {
    this.searchValueMemoDoc = '';
    table.clear();
  }

  public clearRelatedDoc(table: Table) {
    this.searchValueRelatedDoc = '';
    table.clear();
  }

  protected goBack() {
    this.router.navigate([routesConstants.paymentManagement.paymentManagement]);
  }

  ngOnDestroy(): void {
		// prevent memory leak when component destroyed
    this.alive = false;
    if(!this.router.routerState.snapshot.url.includes(routesConstants.paymentManagement.paymentManagement + "/" + routesConstants.paymentManagement.detail)){
      this.searchValueMemoDoc = '';
      this.searchValueRelatedDoc = '';
      localStorage.setItem('memoDocFilter', "");
      localStorage.setItem('relatedDocFilter', "");
    } else {
      localStorage.setItem('memoDocFilter', this.searchValueMemoDoc);
      localStorage.setItem('relatedDocFilter', this.searchValueRelatedDoc);
    }
  }
}
